import { css } from '@emotion/react';
import { Avatar, Box, Chip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import {
  formatShortDate,
  generateAge,
  isBirthDateToday,
  processFilestackUrlSrcSet,
} from '@jebel/utils';

import { UserNameLink } from 'shared/components/symbols';
import { Typography } from 'shared/components/ui';
import { APP_URL } from 'shared/constants';
import { MinimalUserFragment } from 'shared/graphql';
import { useResponsive } from 'shared/hooks';
import { buildUrl } from 'shared/routes';

const AVATAR_SIZE = 40;
const AVATAR_MOBILE_SIZE = 35;

const fullnameCSS = css`
  font-weight: bold;
`;

const cardIconCss = theme => css`
  width: ${theme.breakpoints.down('sm') ? AVATAR_MOBILE_SIZE : AVATAR_SIZE}px;
  height: ${theme.breakpoints.down('sm') ? AVATAR_MOBILE_SIZE : AVATAR_SIZE}px;
  margin-right: ${theme.spacing(1)}px;
`;

const cardCss = theme => css`
  cursor: pointer;

  &:not(:last-child) {
    margin-bottom: ${theme.spacing(2)}px;
  }
`;

export function UserCard({ user }: { user: MinimalUserFragment }) {
  const history = useHistory();
  const { isMobile } = useResponsive();

  const avatarSize = isMobile ? AVATAR_MOBILE_SIZE : AVATAR_SIZE;

  const isBirthdayToday = useMemo(() => {
    return isBirthDateToday(user?.birthDate);
  }, [user?.birthDate]);

  const formatedBirthDate = useMemo(() => {
    return formatShortDate(user?.birthDate);
  }, [user?.birthDate]);

  const age = useMemo(() => {
    if (!user?.birthDate) {
      return null;
    }

    return generateAge(user.birthDate);
  }, [user.birthDate]);

  const onOpenProfile = useCallback(() => {
    if (!user?.id) {
      return;
    }

    const url = buildUrl(APP_URL.user.explorer.profile, {
      pathParams: { id: user?.id },
    });

    history.push(url);
  }, [history, user?.id]);

  return (
    <Box
      onClick={onOpenProfile}
      css={cardCss}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <Box display="flex">
        <Box display="flex" mr={2}>
          <Avatar
            css={cardIconCss}
            srcSet={processFilestackUrlSrcSet(user?.avatar?.downloadUrl || '', {
              compress: true,
              resize: {
                width: avatarSize,
                height: avatarSize,
                fit: 'crop',
              },
            })}
            src={user?.avatar?.downloadUrl || ''}
            variant="circular"
          />
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <UserNameLink user={user} css={fullnameCSS} />
              {isBirthdayToday && <Chip label="Today" size="small" color="secondary" />}
            </Box>

            {user?.birthDate && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="textPrimary">
                  {formatedBirthDate}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  ({age} years old)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
